import { SwapOutlined, UploadOutlined } from '@ant-design/icons';
import { getNetworkErrorMessage } from '@framework/auth';
import {
  CheckboxInput,
  FileUploadInput,
  FormErrorsAlert,
  FormHandler,
  FormItem,
  SubmitButton,
  TextInput,
} from '@framework/form';
import {
  AttorneyDownloadDelegationFileButton,
  ISubmissionManager,
} from '@framework/frontoffice-submission';
import { Drawer, DrawerButtonRow } from '@framework/ui';
import { yup } from '@framework/validation';
import { Button, Space, Tooltip, message } from 'antd';
import { RcFile } from 'antd/es/upload';
import { useCallback, useMemo, useState } from 'react';
import { useChangeSubmissionManager } from '../hooks/useChangeSubmissionManager';

import AddressBookButton from '#build-flags/routes/address-book/button/AddressBookButton';
import { useTranslation } from '@framework/i18n';

const TFormItem = FormItem as typeof FormItem<ChangeSubmissionManagerValues>;

const ChangeSubmissionManagerSchema = yup
  .object()
  .shape({
    fiscalCode: yup.string().required().label('Codice Fiscale'),
    canSubmit: yup.boolean().required().default(false).label('Può Inviare'),
    file: yup
      .mixed<RcFile>()
      // Verifichiamo che il file sia richiesto
      .when('$requiresFile', ([requiresFile], schema) =>
        requiresFile ? schema.required() : schema,
      )
      .label('Documento di Delega'),
  })
  .required()
  .noUnknown();

export type ChangeSubmissionManagerValues = yup.InferType<
  typeof ChangeSubmissionManagerSchema
>;

export interface IChangeSubmissionManagerButtonProps {
  submissionId: string | null;
  type: 'manager' | 'attorney';
  iconOnly?: boolean;
  data: ISubmissionManager | null | undefined;
  requiresFile?: boolean | null;
}

export function ChangeSubmissionManagerButton(
  props: IChangeSubmissionManagerButtonProps,
) {
  const { submissionId, iconOnly, type, data, requiresFile } = props;
  const { t } = useTranslation();
  const { update } = useChangeSubmissionManager(type);

  const subjectName =
    type === 'manager'
      ? t('change-submission-manager-button.subject-name.gestore', 'Gestore')
      : t(
          'change-submission-manager-button.subject-name.procuratore',
          'Procuratore',
        );

  const [open, setOpen] = useState(false);
  const handleClose = useCallback(() => setOpen(false), []);

  const handleSubmit = useCallback(
    async (values: ChangeSubmissionManagerValues) => {
      if (!submissionId) {
        console.error('No submissionId');
        message.error(
          t(
            'change-submission-manager-button.message.error.nessun-id-della-richiesta-trovato',
            'Nessun ID della richiesta trovato',
          ),
        );
        return;
      }
      try {
        const result = await update.mutateAsync(submissionId, values);
        if (!result) return;

        message.success(
          `${subjectName} ${t(
            'change-submission-manager-button.message.success.aggiornato-con-successo',
            'aggiornato con successo',
          )}`,
        );

        setOpen(false);
      } catch (e) {
        console.log(e);
        message.error(
          getNetworkErrorMessage(
            e,
            t(
              'change-submission-manager-button.message.error.si-e-verificato-un-errore-riprovare',
              'Si è verificato un errore. Riprovare.',
            ),
          ),
        );
      }
    },
    [submissionId, update, subjectName, t],
  );

  const title = useMemo(() => {
    if (data?.fiscalCode)
      return `${t(
        'change-submission-manager-button.title-prexix.cambia',
        'Cambia',
      )} ${subjectName}`;
    return `${t(
      'change-submission-manager-button.title-prexix.assegna-un',
      'Assegna un',
    )} ${subjectName}`;
  }, [data?.fiscalCode, subjectName, t]);

  // const documentId = useWatch({ name: 'file' });
  // const filename = useMemo(() => {
  //   if (!documentId) return null;
  //   return documentId.split('/').pop();
  // }, [documentId]);

  return (
    <>
      <Tooltip title={title}>
        <Button
          icon={<SwapOutlined />}
          onClick={() => {
            setOpen(true);
          }}
          children={iconOnly ? undefined : title}
          type={iconOnly ? 'link' : 'default'}
        />
      </Tooltip>
      <Drawer
        title={title}
        open={open}
        onClose={handleClose}
        destroyOnClose
        size="large"
      >
        <FormHandler
          nested
          layout="vertical"
          schema={ChangeSubmissionManagerSchema}
          initialValues={data ?? {}}
          onSubmit={handleSubmit}
          validationContext={{ requiresFile }}
        >
          <FormErrorsAlert />

          <TFormItem
            name={'fiscalCode'}
            label={t(
              'change-submission-manager-button.input.fiscal-code.label.codice-fiscale',
              'Codice Fiscale o E-Mail',
            )}
          >
            <Space.Compact size="large" block>
              <TextInput
                name={'fiscalCode'}
                placeholder={t(
                  'change-submission-manager-button.input.fiscal-code.placeholder.codice-fiscale',
                  'Codice Fiscale o E-Mail',
                )}
              />
              {AddressBookButton && (
                <AddressBookButton
                  idFieldName="fiscalCode"
                  onSelect={(record, close, setValue) => {
                    setValue(
                      record.codiceFiscale ?? record.email ?? '<unknown>',
                    );
                    close();
                  }}
                />
              )}
            </Space.Compact>
          </TFormItem>

          {type === 'attorney' && (
            <>
              <TFormItem
                name="canSubmit"
                label={t(
                  'change-submission-manager-button.input.can-submit.label.invio-richiesta',
                  'Invio richiesta',
                )}
                help={t(
                  'change-submission-manager-button.input.can-submit.help.permetti-al-procuratore-indicato-di-inviare-la-richiesta',
                  'Permetti al procuratore indicato di inviare la richiesta',
                )}
              >
                <CheckboxInput name="canSubmit" />{' '}
                {t(
                  'change-submission-manager-button.input.can-submit.checkbox-text.permesso-di-invio',
                  'Permesso di invio',
                )}
              </TFormItem>
              <TFormItem
                name="file"
                label={t(
                  'change-submission-manager-button.input.file.label.documento-di-delega',
                  'Documento di Delega',
                )}
              >
                <FileUploadInput
                  name="file"
                  title={t(
                    'change-submission-manager-button.input.file.upload-button.title.carica-documento-di-delega',
                    'Carica Documento di Delega',
                  )}
                  buttonProps={{
                    icon: (
                      <UploadOutlined
                        aria-hidden
                        aria-label={t(
                          'change-submission-manager-button.input.file.upload-button.icon-aria-label.carica',
                          'Carica',
                        )}
                      />
                    ),
                  }}
                />
              </TFormItem>

              <AttorneyDownloadDelegationFileButton
                submissionId={props.submissionId!}
                fileId={data?.uploadedFileId}
                label={t(
                  'change-submission-manager-button.input.file.label.documento-di-delega',
                  'Documento di Delega',
                )}
              />
            </>
          )}

          <DrawerButtonRow>
            <Space>
              <Button size="large" onClick={handleClose}>
                {t('common.annulla', 'Annulla')}
              </Button>
              <SubmitButton />
            </Space>
          </DrawerButtonRow>
        </FormHandler>
      </Drawer>
    </>
  );
}
